import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { ChatBubbleLeftEllipsisIcon, ClockIcon, CheckIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { AsignadosBubbles } from '../AsignadoBubble';
import {
  OT_TIPOS, folioTexto, diasEnFase, normalizarAsignados, faseEstaTerminada,
} from '../../utils/ordenesTrabajo';

// Badge colors: the two base tipos keep theirs, every org-defined tipo reads
// neutral so a third color is never mistaken for a state.
export const tipoBadgeClass = (tipo) => (
  tipo === OT_TIPOS.ENDOSO ? 'bg-amber-50 text-amber-700'
    : tipo === OT_TIPOS.FIANZA ? 'bg-cian-digital/10 text-azul-surelia'
      : 'bg-zinc-100 text-zinc-700'
);

/**
 * One OT in the tablero. Drag to move between fases; click to open.
 *
 * @param {Object} props
 * @param {Object} props.ot  - decorated row from useOrdenesTrabajo
 * @param {Array<Object>} props.miembros
 * @param {Function} props.onClick
 * @param {boolean} [props.overlay]  - rendered inside the DragOverlay
 * @param {boolean} [props.arrastrable]  - false for members who cannot run the relevo
 */
export function OtTarjeta({ ot, miembros = [], onClick, overlay = false, arrastrable = true }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: ot.id, disabled: overlay || !arrastrable });

  const style = overlay ? undefined : {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };

  const dias = diasEnFase(ot);
  // No name of its own: the fiado is the headline, contrato and fianza below.
  const detalle = [ot.contractNumber ? `Contrato ${ot.contractNumber}` : ot.contractTitle, ot.fianzaNumero ? `Fianza #${ot.fianzaNumero}` : '']
    .filter(Boolean).join(' · ');
  const terminada = faseEstaTerminada(ot);
  const vencida = ot.fechaCompromiso && Number(ot.fechaCompromiso) < Date.now();
  // Against the fase's días objetivo (decorated by useOrdenesTrabajo).
  const faseVencida = ot.faseEstado === 'vencida';
  const fasePorVencer = ot.faseEstado === 'porVencer';

  return (
    <div
      ref={overlay ? undefined : setNodeRef}
      style={style}
      {...(overlay || !arrastrable ? {} : attributes)}
      {...(overlay || !arrastrable ? {} : listeners)}
      onClick={() => onClick?.(ot)}
      className={clsx(
        'rounded-lg border bg-white p-3 shadow-sm select-none',
        arrastrable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        isDragging && !overlay ? 'opacity-40' : 'hover:border-azul-surelia/50',
        overlay ? 'border-azul-surelia shadow-lg rotate-1' : 'border-zinc-200',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] font-semibold tabular-nums text-azul-surelia">{folioTexto(ot.folio)}</span>
        <span className={clsx('rounded-full px-2 py-0.5 text-[10px] font-medium', tipoBadgeClass(ot.tipo))}>
          {ot.tipoNombre || ot.tipo}
        </span>
      </div>
      <div className={clsx('mt-1 text-sm font-medium leading-snug', ot.clientName ? 'text-zinc-800' : 'text-zinc-400')}>{ot.clientName || 'Sin fiado todavía'}</div>
      {detalle && (
        <div className="mt-0.5 text-xs text-zinc-500 truncate" title={ot.contractTitle || detalle}>{detalle}</div>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <AsignadosBubbles uids={normalizarAsignados(ot)} miembros={miembros} size="sm" max={3} />
        <div className="flex items-center gap-2 text-[11px] text-zinc-500">
          {ot.faseAprobacion && !terminada && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700" title="Espera la aprobación de un aprobador">
              <ShieldCheckIcon className="w-3 h-3" />Aprobación
            </span>
          )}
          {terminada && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700" title={`Fase terminada${ot.faseTerminada?.nombre ? ` por ${ot.faseTerminada.nombre}` : ''}, por asignar`}>
              <CheckIcon className="w-3 h-3" />Terminada
            </span>
          )}
          {Number(ot.mensajes) > 0 && (
            <span className="inline-flex items-center gap-0.5" title="Mensajes">
              <ChatBubbleLeftEllipsisIcon className="w-3.5 h-3.5" />{ot.mensajes}
            </span>
          )}
          <span
            className={clsx('inline-flex items-center gap-0.5 tabular-nums', (vencida || faseVencida) ? 'text-red-600 font-medium' : fasePorVencer ? 'text-amber-600' : '')}
            title={vencida ? 'Pasó la fecha compromiso' : ot.faseObjetivo ? `Días en esta fase, objetivo ${ot.faseObjetivo}` : 'Días en esta fase'}
          >
            <ClockIcon className="w-3.5 h-3.5" />{dias}{ot.faseObjetivo ? `/${ot.faseObjetivo}` : ''} d
          </span>
        </div>
      </div>
    </div>
  );
}

export default OtTarjeta;
